/**
 * DetailScreen — muestra el detalle de un item seleccionado
 * (announcement, incident, vote, fee). Reutilizable.
 *
 * Navigation: D-pad vertical para navegar entre items cuando aplique
 * (ej. options de un vote), A = accion principal, B = back.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '@/state/store';
import { sfx } from '@/lib/audio';
import {
  announcementsApi,
  incidentsApi,
  votesApi,
  feesApi,
  type Announcement,
  type Incident,
  type Vote,
  type Fee,
  ApiError,
} from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { CheckSprite, XSprite, ArrowSprite } from '@/components/Sprites';

export function DetailScreen() {
  const { detail, closeDetail, showToast, user } = useApp();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Announcement | Incident | Vote | Fee | null>(null);
  const [selectedOption, setSelectedOption] = useState(0);
  const handlersRef = useRef({ data, selectedOption, closeDetail, showToast, isAdmin });

  const load = useCallback(async () => {
    if (!detail) return;
    setLoading(true);
    try {
      let result;
      if (detail.type === 'announcement') {
        const r = await announcementsApi.get(detail.id);
        result = r.announcement;
      } else if (detail.type === 'incident') {
        const r = await incidentsApi.get(detail.id);
        result = r.incident;
      } else if (detail.type === 'vote') {
        const r = await votesApi.get(detail.id);
        result = r.vote;
      } else {
        // fee: no hay endpoint detail, usar el de list
        const r = await feesApi.list();
        result = r.fees.find((f) => f.id === detail.id);
      }
      setData(result || null);
    } catch (err) {
      if (err instanceof ApiError) showToast(err.message, 'error');
      else showToast('Error al cargar', 'error');
    } finally {
      setLoading(false);
    }
  }, [detail, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleA = useCallback(async () => {
    if (!data) return;
    sfx.confirm();
    if (detail?.type === 'vote' && !('has_voted' in data && data.has_voted) && (data as Vote).can_vote !== false) {
      const vote = data as Vote;
      const opt = vote.options[selectedOption];
      if (!opt) return;
      try {
        const res = await votesApi.cast(vote.id, selectedOption);
        if (res.success) {
          showToast('¡Voto registrado!', 'success');
          load();
        } else {
          showToast(res.error || 'Error', 'error');
        }
      } catch (err) {
        showToast('Error al votar', 'error');
      }
    } else if (detail?.type === 'fee' && 'status' in data && data.status !== 'paid') {
      const fee = data as Fee;
      try {
        const res = await feesApi.markPaid(fee.id, fee.amount, 'cash');
        if (res.success) {
          showToast('¡Pago enviado! (pendiente de confirmar)', 'success');
          load();
        } else {
          showToast(res.error || 'Error', 'error');
        }
      } catch (err) {
        showToast('Error al pagar', 'error');
      }
    } else if (detail?.type === 'incident' && 'status' in data && isAdmin && data.status !== 'cancelled') {
      const inc = data as Incident;
      const states: Incident['status'][] = ['reported', 'in_progress', 'resolved'];
      const idx = states.indexOf(inc.status);
      const next = states[(idx + 1) % states.length];
      try {
        const res = await incidentsApi.update(inc.id, { status: next });
        if (res.success) {
          showToast(`Estado: ${next}`, 'success');
          load();
        }
      } catch (err) {
        showToast('Error', 'error');
      }
    }
  }, [data, detail, selectedOption, showToast, load, isAdmin]);

  const handleUpDown = useCallback((delta: number) => {
    if (data && detail?.type === 'vote' && !('has_voted' in data && data.has_voted)) {
      const vote = data as Vote;
      const next = Math.max(0, Math.min(vote.options.length - 1, selectedOption + delta));
      if (next !== selectedOption) {
        setSelectedOption(next);
        sfx.tick();
      }
    }
  }, [data, detail, selectedOption]);

  handlersRef.current = { data, selectedOption, closeDetail, showToast, isAdmin };

  // Hardware buttons
  useEffect(() => {
    const onUp = () => handlersRef.current.data && handlersRef.current.selectedOption !== undefined && handleUpDown(-1);
    const onDown = () => handlersRef.current.data && handlersRef.current.selectedOption !== undefined && handleUpDown(1);
    const onA = () => handleA();
    const onB = () => { sfx.cancel(); closeDetail(); };
    window.addEventListener('vicin-gb:up', onUp);
    window.addEventListener('vicin-gb:down', onDown);
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:up', onUp);
      window.removeEventListener('vicin-gb:down', onDown);
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, [handleUpDown, handleA, closeDetail]);

  const titlePrefix = detail ? {
    announcement: 'NOTICIA',
    incident: 'REPORTE',
    vote: 'VOTACIÓN',
    fee: 'GASTO',
  }[detail.type] : '';

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">{titlePrefix || 'VICIN'}</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>[B] VOLVER</span>
        </div>
      </div>

      <div className="gb-body" style={{ padding: '4px 4px', overflow: 'hidden' }}>
        {loading ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <div className="gb-pix gb-pix-xs gb-mid-text">CARGANDO...</div>
          </div>
        ) : !data ? (
          <div className="gb-center" style={{ gap: 8 }}>
            <XSprite size={32} />
            <div className="gb-pix gb-pix-xs gb-mid-text">NO ENCONTRADO</div>
          </div>
        ) : (
          <div className="gb-stack" style={{ gap: 5, height: '100%' }}>
            <DetailContent
              data={data}
              detail={detail}
              selectedOption={selectedOption}
              onSelectOption={setSelectedOption}
              isAdmin={isAdmin}
              onAction={handleA}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DetailContent({
  data,
  detail,
  selectedOption,
  onSelectOption,
  isAdmin,
  onAction,
}: {
  data: Announcement | Incident | Vote | Fee;
  detail: { id: string; type: 'announcement' | 'incident' | 'vote' | 'fee' };
  selectedOption: number;
  onSelectOption: (n: number) => void;
  isAdmin: boolean;
  onAction: () => void;
}) {
  if (detail.type === 'announcement') {
    const a = data as Announcement;
    return (
      <>
        <div className="gb-pix" style={{ fontSize: 8, lineHeight: 1.3, color: 'var(--gb-dark)' }}>
          {a.title}
        </div>
        <div className="gb-pix gb-mid-text" style={{ fontSize: 5 }}>
          {formatRelative(a.created_at)} · {a.priority === 'urgent' ? 'URGENTE' : a.priority === 'important' ? 'IMPORTANTE' : 'NORMAL'}
        </div>
        <div className="gb-divider" />
        <div
          className="gb-pix"
          style={{
            fontSize: 6,
            lineHeight: 1.4,
            color: 'var(--gb-dark)',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {a.body || '(sin contenido)'}
        </div>
        {a.author_name && (
          <div className="gb-pix gb-mid-text" style={{ fontSize: 5, textAlign: 'right' }}>
            — {a.author_name}
          </div>
        )}
      </>
    );
  }

  if (detail.type === 'incident') {
    const inc = data as Incident;
    const statusLabel: Record<Incident['status'], string> = {
      reported: 'REPORTADO',
      in_progress: 'EN CURSO',
      resolved: 'RESUELTO',
      cancelled: 'CANCELADO',
    };
    const statusVariant: Record<Incident['status'], 'success' | 'pending' | 'info' | 'danger'> = {
      reported: 'danger',
      in_progress: 'pending',
      resolved: 'success',
      cancelled: 'info',
    };
    return (
      <>
        <div className="gb-pix" style={{ fontSize: 8, lineHeight: 1.3, color: 'var(--gb-dark)' }}>
          {inc.title}
        </div>
        <div className="gb-row" style={{ gap: 4, alignItems: 'center' }}>
          <div
            className={`gb-list-badge gb-list-badge--${statusVariant[inc.status]}`}
            style={{
              padding: '1px 4px',
              fontSize: 5,
            }}
          >
            {statusLabel[inc.status]}
          </div>
          <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
            {inc.category} · {formatRelative(inc.created_at)}
          </div>
        </div>
        <div className="gb-divider" />
        <div
          className="gb-pix"
          style={{
            fontSize: 6,
            lineHeight: 1.4,
            color: 'var(--gb-dark)',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {inc.description || '(sin descripción)'}
        </div>
        {isAdmin && inc.status !== 'cancelled' && (
          <button
            onClick={onAction}
            className="gb-action"
            data-testid="detail-incident-advance"
            style={{ marginTop: 4, alignSelf: 'center', fontSize: 7, padding: '4px 10px' }}
          >
            ▶ AVANZAR ESTADO
          </button>
        )}
      </>
    );
  }

  if (detail.type === 'vote') {
    const v = data as Vote;
    const total = v.total_votes ?? v.options.reduce((s, o) => s + o.votes, 0);
    return (
      <>
        <div className="gb-pix" style={{ fontSize: 8, lineHeight: 1.3, color: 'var(--gb-dark)' }}>
          {v.title}
        </div>
        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
          {v.has_voted ? '✓ VOTASTE' : 'CIERRA ' + formatRelative(v.ends_at)}
        </div>
        <div className="gb-divider" />
        <div
          className="gb-stack"
          style={{ gap: 3, overflowY: 'auto', flex: 1 }}
        >
          {v.options.map((opt, i) => {
            const sel = i === selectedOption;
            const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
            return (
              <button
                key={i}
                className="gb-menu-item"
                data-selected={sel}
                style={{ position: 'relative', padding: '3px 6px' }}
                onClick={() => onSelectOption(i)}
                onMouseEnter={() => onSelectOption(i)}
              >
                {v.has_voted && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 4,
                      top: 0,
                      bottom: 0,
                      width: `${pct}%`,
                      background: 'rgba(15, 56, 15, 0.2)',
                      transition: 'width 200ms',
                    }}
                  />
                )}
                <span className="gb-menu-num" style={{ position: 'relative' }}>{i + 1}</span>
                <span style={{ flex: 1, position: 'relative', fontSize: 6 }}>{opt.text}</span>
                <span
                  className="gb-pix gb-mid-text"
                  style={{ fontSize: 5, position: 'relative' }}
                >
                  {opt.votes} ({pct}%)
                </span>
                {sel && !v.has_voted && <ArrowSprite size={8} dir="right" />}
              </button>
            );
          })}
        </div>
        {!v.has_voted && v.can_vote !== false && (
          <>
            <button
              onClick={onAction}
              className="gb-action"
              data-testid="detail-vote-cast"
              style={{ marginTop: 4, alignSelf: 'center', fontSize: 7, padding: '4px 10px' }}
            >
              ▶ VOTAR
            </button>
            <div className="gb-pix gb-mid-text" style={{ fontSize: 5, textAlign: 'center', marginTop: 2 }}>
              [▲▼] ELEGIR · [A] VOTAR
            </div>
          </>
        )}
      </>
    );
  }

  // fee
  const f = data as Fee;
  const statusVariant: Record<Fee['status'], 'success' | 'pending' | 'danger'> = {
    paid: 'success',
    pending: 'pending',
    overdue: 'danger',
  };
  return (
    <>
      <div className="gb-pix" style={{ fontSize: 8, lineHeight: 1.3, color: 'var(--gb-dark)' }}>
        {f.description || `GASTO ${f.period}`}
      </div>
      <div className="gb-row" style={{ gap: 4, alignItems: 'center' }}>
        <div
          className={`gb-list-badge gb-list-badge--${statusVariant[f.status]}`}
          style={{
            padding: '1px 4px',
            fontSize: 5,
          }}
        >
          {f.status === 'paid' ? 'PAGADO' : f.status === 'overdue' ? 'VENCIDO' : 'PENDIENTE'}
        </div>
        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5 }}>
          VENCE {f.due_date}
        </div>
      </div>
      <div className="gb-divider" />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          gap: 8,
        }}
      >
        <div
          className="gb-pix"
          style={{
            fontSize: f.amount > 9999 ? 12 : 18,
            color: 'var(--gb-dark)',
            letterSpacing: 2,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ${f.amount}
        </div>
        <div className="gb-pix gb-mid-text" style={{ fontSize: 5 }}>
          PERÍODO {f.period}
        </div>
      </div>
      {f.status !== 'paid' && (
        <button
          onClick={onAction}
          className="gb-action"
          data-testid="detail-fee-pay"
          style={{ marginTop: 4, alignSelf: 'center', fontSize: 7, padding: '4px 10px' }}
        >
          ▶ SOLICITAR PAGO
        </button>
      )}
      {f.status === 'paid' && f.paid_at && (
        <div className="gb-pix gb-mid-text" style={{ fontSize: 5, textAlign: 'center' }}>
          PAGADO EL {f.paid_at}
        </div>
      )}
    </>
  );
}